import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { ProtoSelectStandalone } from 'src/components/flow-editor/ProtoSelectStandalone'
import { ToolbarRow } from 'src/components/payments-ops/ToolbarRow'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { TableSearch } from 'src/components/shared/table/TableSearch'
import { BankOnboardingStatus } from 'src/constants/payments-ops/enums'
import { bankOnboardingStatusLabels } from 'src/constants/payments-ops/labels'

interface Props {
  search: string
  status: BankOnboardingStatus | null
  onSearchChange(search: string): void
  onStatusChange(status: BankOnboardingStatus | null): void
  onRefresh(): void
}

const ALL_STATUSES_VALUE = ''

const BankOnboardingsToolbarComponent = ({
  search,
  status,
  onSearchChange,
  onStatusChange,
  onRefresh,
}: Props) => {
  const statusOptions = useMemo(() => {
    const options = Object.values(BankOnboardingStatus).map((value) => {
      return { value, label: bankOnboardingStatusLabels[value].label }
    })

    return [{ value: ALL_STATUSES_VALUE, label: 'All statuses' }, ...options]
  }, [])

  const handleStatusChange = useCallback(
    (value: string | number) => {
      const nextStatus = String(value)

      onStatusChange(
        nextStatus === ALL_STATUSES_VALUE ? null : (nextStatus as BankOnboardingStatus),
      )
    },
    [onStatusChange],
  )

  return (
    <ToolbarRow>
      {/* Server-side, partner name only (the backend matches nothing else). */}
      <TableSearch
        searchQuery={search}
        onChangeSearchQuery={onSearchChange}
        width={280}
        inputDataTestId="bank-onboarding-search"
      />
      <div css={tw`flex w-[240px] flex-col`}>
        <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
          Status
        </Typography>
        <ProtoSelectStandalone
          value={status ?? ALL_STATUSES_VALUE}
          options={statusOptions}
          placeholder="All statuses"
          onValueChange={handleStatusChange}
        />
      </div>
      <div css={tw`ml-auto pb-1`}>
        <Button
          size="small"
          color="white"
          leftIcon={<GSIcon iconName="gs_arrows_repeat" size={20} css={tw`text-shades-1000`} />}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </div>
    </ToolbarRow>
  )
}

export const BankOnboardingsToolbar = memo(BankOnboardingsToolbarComponent)
